import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ToastService } from '@black/ui';
import { QuizService } from '../../shared/core/services/quiz.service';
import { Quiz, QuizSubmission } from '../../shared/models/quiz.model';

@Component({
  selector: 'black-student-quizzes',
  standalone: false,
  templateUrl: './student-quizzes.component.html',
})
export class StudentQuizzesComponent implements OnInit, OnChanges, OnDestroy {
  private quizService = inject(QuizService);
  private toastService = inject(ToastService);

  @Input() liveClassId: string | null = null;
  @Input() isAuthorized = true;
  @Input() isPending = false;

  quizzes: Quiz[] = [];
  loading = false;

  // Active Quiz taking state
  activeQuiz: Quiz | null = null;
  activeQuestionIndex = 0;
  selectedAnswers: Map<string, string> = new Map(); // questionId -> selectedOptionId
  submitting = false;

  // Timer
  timerInterval: any = null;
  remainingSeconds = 0;

  // Completed Submission / Result State
  latestSubmission: QuizSubmission | null = null;
  showResult = false;

  // Past submissions per quiz
  pastSubmissionsMap: Map<string, QuizSubmission[]> = new Map();

  ngOnInit(): void {
    this.loadQuizzes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['liveClassId'] && !changes['liveClassId'].isFirstChange()) {
      this.loadQuizzes();
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  loadQuizzes(): void {
    if (!this.liveClassId) return;
    this.loading = true;
    this.quizService.getQuizzesByLiveClass(this.liveClassId).subscribe({
      next: (data) => {
        this.quizzes = data;
        this.loading = false;
        // Fetch student's own attempts for each quiz
        this.quizzes.forEach((q) => {
          this.quizService.getMySubmissions(q._id).subscribe({
            next: (subs) => {
              this.pastSubmissionsMap.set(q._id, subs);
            },
          });
        });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getAttemptsForQuiz(quizId: string): QuizSubmission[] {
    return this.pastSubmissionsMap.get(quizId) || [];
  }

  getBestScore(quizId: string): number | null {
    const attempts = this.getAttemptsForQuiz(quizId);
    if (attempts.length === 0) return null;
    return Math.max(...attempts.map((a) => a.totalScore));
  }

  isWindowOpen(quiz: Quiz): boolean {
    const now = new Date();
    if (quiz.availableFrom && now < new Date(quiz.availableFrom)) {
      return false;
    }
    if (quiz.availableTo && now > new Date(quiz.availableTo)) {
      return false;
    }
    return true;
  }

  getWindowStatus(
    quiz: Quiz,
  ): { status: 'open' | 'upcoming' | 'closed'; message: string } | null {
    const now = new Date();
    if (quiz.availableFrom && now < new Date(quiz.availableFrom)) {
      return {
        status: 'upcoming',
        message: `Opens ${new Date(quiz.availableFrom).toLocaleDateString()} ${new Date(quiz.availableFrom).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      };
    }
    if (quiz.availableTo && now > new Date(quiz.availableTo)) {
      return {
        status: 'closed',
        message: 'Access window closed',
      };
    }
    if (quiz.availableTo) {
      return {
        status: 'open',
        message: `Open until ${new Date(quiz.availableTo).toLocaleDateString()} ${new Date(quiz.availableTo).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      };
    }
    return null;
  }

  canAttempt(quiz: Quiz): boolean {
    if (!this.isAuthorized) return false;
    if (!this.isWindowOpen(quiz)) return false;
    const max = quiz.maxAttempts ?? 1;
    if (max <= 0) return true; // unlimited
    const attempts = this.getAttemptsForQuiz(quiz._id);
    return attempts.length < max;
  }

  startQuiz(quiz: Quiz): void {
    if (!this.isAuthorized) {
      this.toastService.showToast(
        'Subscription required to attempt quizzes',
        'warning',
      );
      return;
    }
    if (!this.isWindowOpen(quiz)) {
      this.toastService.showToast(
        'Quiz is currently outside its access window',
        'warning',
      );
      return;
    }
    if (!this.canAttempt(quiz)) {
      this.toastService.showToast(
        'You have reached the maximum number of attempts for this quiz',
        'warning',
      );
      return;
    }

    this.activeQuiz = quiz;
    this.activeQuestionIndex = 0;
    this.selectedAnswers.clear();
    this.latestSubmission = null;
    this.showResult = false;

    if (quiz.durationMinutes && quiz.durationMinutes > 0) {
      this.remainingSeconds = quiz.durationMinutes * 60;
      this.startTimer();
    } else {
      this.remainingSeconds = 0;
    }
  }

  startTimer(): void {
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.remainingSeconds > 0) {
        this.remainingSeconds--;
      } else {
        this.clearTimer();
        this.toastService.showToast(
          'Time is up! Submitting quiz...',
          'warning',
        );
        this.submitQuizAnswers();
      }
    }, 1000);
  }

  clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  get formattedTime(): string {
    const mins = Math.floor(this.remainingSeconds / 60);
    const secs = this.remainingSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  selectOption(questionId: string, optionId: string): void {
    this.selectedAnswers.set(questionId, optionId);
  }

  isOptionSelected(questionId: string, optionId: string): boolean {
    return this.selectedAnswers.get(questionId) === optionId;
  }

  nextQuestion(): void {
    if (
      this.activeQuiz &&
      this.activeQuestionIndex < this.activeQuiz.questions.length - 1
    ) {
      this.activeQuestionIndex++;
    }
  }

  prevQuestion(): void {
    if (this.activeQuestionIndex > 0) {
      this.activeQuestionIndex--;
    }
  }

  submitQuizAnswers(): void {
    if (!this.activeQuiz || this.submitting) return;

    this.clearTimer();
    this.submitting = true;

    const answersPayload = this.activeQuiz.questions.map((q) => {
      const selected = this.selectedAnswers.get(q.id);
      return {
        questionId: q.id,
        selectedOptionIds: selected ? [selected] : [],
      };
    });

    this.quizService.submitQuiz(this.activeQuiz._id, answersPayload).subscribe({
      next: (submission) => {
        this.latestSubmission = submission;
        this.showResult = true;
        this.submitting = false;

        // Update past submissions
        const existing =
          this.pastSubmissionsMap.get(this.activeQuiz!._id) || [];
        this.pastSubmissionsMap.set(this.activeQuiz!._id, [
          submission,
          ...existing,
        ]);

        this.toastService.showToast(
          submission.passed
            ? 'Congratulations! You passed!'
            : 'Quiz submitted.',
          submission.passed ? 'success' : 'info',
        );
      },
      error: (err) => {
        this.submitting = false;
        this.toastService.showToast(
          err?.error?.message || 'Failed to submit quiz',
          'error',
        );
      },
    });
  }

  closeQuizModal(close: () => void): void {
    this.clearTimer();
    this.activeQuiz = null;
    this.showResult = false;
    close();
  }
}
